import CustomModal from '@components/CustomModal';
import DropdownMenu from '@components/DropdownMenu';
import CreateEditUserModal from '@components/modals/CreateEditUserModal';
import DisableUserModal from '@components/modals/DisableUserModal';
import EnableUserModal from '@components/modals/EnableUserModal';
import { EditIcon24 } from '@components/ui/WolfIcons';
import WolfSearch from '@components/ui/WolfSearch';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient, updateHeaders } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Organization } from '@models/organization.model';
import { getSelfWithOrganizationRequest, User, UserLocale, UserRoles, UserStatus } from '@models/user.model';
import { NoAccounts, Person2Outlined, RestartAltOutlined, RestoreFromTrash } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setImpersonate } from '@redux/reducers/impersonate.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import {
  setImpersonating,
  setUserDetails,
  setUserOrganizations,
  updateSidebarStatus
} from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

// TODO: With some work this could become generic if we replace documents for a generic type

interface UserListProps {
  handleDisableUser: (userId: string) => void;
  handleEnableUser: (userId: string) => void;
  handleEditUser: (
    organizationId: string,
    email: string,
    firstName: string,
    lastName: string,
    userId: string,
    role?: UserRoles,
    seatAssignmentId?: string,
    currentRole?: UserRoles,
  ) => void;
  // common
  count: number;
  users: User[];
  organizations: Organization[];
  // search
  search: string;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // filter
  filter: string;
  handleFilterChange: (filter: string) => void;
  // order
  orderDirection: 'asc' | 'desc';
  orderBy: string | null;
  handleRequestSort: (property: string) => void;
  // pagination
  page: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetFailedLoginCount: (userId: string) => void;
}

const UserList: React.FC<UserListProps> = ({
  count,
  users,
  organizations,
  search,
  handleSearchChange,
  filter,
  handleFilterChange,
  orderBy,
  orderDirection,
  handleRequestSort,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  handleDisableUser,
  handleEnableUser,
  handleEditUser,
  resetFailedLoginCount,
}) => {
  const [showConfirmDisableModal, setShowConfirmDisableModal] = useState<boolean>(false);
  const [showConfirmEnableModal, setShowConfirmEnableModal] = useState<boolean>(false);
  //TODO: Unify these
  const [userToDisable, setuserToDisable] = useState<User | undefined>(undefined);
  const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
  const [userToEnable, setuserToEnable] = useState<User | undefined>(undefined);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const localeUsers = useTranslation('users');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { collapsedSidebar, role } = useAppSelector((state: RootState) => state.user);
  const { organizationId, name: organizationName } = useAppSelector((state: RootState) => state.organization);

  const toggleConfirmDeleteModal = (user?: User) => {
    setuserToDisable(user);
    setShowConfirmDisableModal(true);
  };

  const toggleEnableConfirmModal = (user?: User) => {
    setuserToEnable(user);
    setShowConfirmEnableModal(true);
  };

  const toggleEditModal = (user: User) => {
    setUserToEdit(user);
    setShowEditModal(true);
  };

  const disableUser = () => {
    if (showConfirmDisableModal) {
      handleDisableUser(userToDisable?.userId || '');
    }
    setShowConfirmDisableModal(false);
    setuserToDisable(undefined);
  };

  const enableUser = () => {
    if (showConfirmEnableModal) {
      handleEnableUser(userToEnable?.userId || '');
    }
    setShowConfirmEnableModal(false);
    setuserToEnable(undefined);
  };

  const modalClose = () => {
    setShowConfirmDisableModal(false);
    setShowEditModal(false);
  };

  const handleImpersonate = async (id?: string, name?: string) => {
    try {
      if (!id || !name) return;

      // Store current organization data before impersonation
      const currentOrgId = organizationId;
      const currentOrgName = organizationName;

      updateHeaders({ Impersonate: `Target ${id}` });
      if (collapsedSidebar) {
        dispatch(updateSidebarStatus());
      }
      dispatch(
        setImpersonate({
          name,
          id,
          originalOrganizationId: currentOrgId || '',
          originalOrganizationName: currentOrgName || '',
        }),
      );
      dispatch(setImpersonating(true));

      const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );
      const user = userData.getSelfWithOrganization.user;
      const role = userData.getSelfWithOrganization.role;
      const organization = userData.getSelfWithOrganization.organization;
      const organizations = userData.getSelfWithOrganization.organizations || [];

      if (organization) {
        dispatch(setOrganizationData(organization));
      }
      dispatch(setUserDetails({ ...user, role }));
      dispatch(setUserOrganizations(organizations));
      dispatch(setLocale(user.locale || UserLocale.EN));

      // Navigate to processes page after successful impersonation
      history.push('/processes');
    } catch (e) {
      dispatch(appendActionMessage({ message: localeActionMessages['impersonateFailed'], type: SnackType.ERROR }));
    }
  };

  const sortedTableHeaderCell = ({
    label,
    property,
    align,
  }: {
    label: string;
    property: string;
    align?: 'right' | 'left' | 'center' | 'inherit' | 'justify' | undefined;
  }) => {
    return (
      <TableCell sortDirection={orderBy === property ? orderDirection : false} align={align ?? 'inherit'}>
        <TableSortLabel
          active={orderBy === property}
          direction={orderBy === property ? orderDirection : 'asc'}
          onClick={() => handleRequestSort(property)}>
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  const activeFilterItems = [
    { value: 'All', label: localeCommon['all'] },
    { value: 'Active', label: localeCommon['active'] },
    { value: 'Disabled', label: localeCommon['disabled'] },
  ];

  return (
    <>
      <Box>
        <Box className="flex items-center mb-2">
          <WolfSearch value={search} onChange={handleSearchChange} />
          <DropdownMenu items={activeFilterItems} applyFilter={handleFilterChange} filter={filter} />
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader>
            <TableHead>
              <TableRow>
                {sortedTableHeaderCell({ label: localeCommon['name'], property: 'name' })}
                {sortedTableHeaderCell({ label: localeCommon['email'], property: 'email' })}
                {role !== UserRoles.SUPER_ADMIN && sortedTableHeaderCell({ label: localeUsers['role'], property: 'role' })}
                {role === UserRoles.SUPER_ADMIN &&
                  sortedTableHeaderCell({ label: localeUsers['failedLoginAttempts'], property: 'failedLoginAttempts' })}
                {role === UserRoles.SUPER_ADMIN && <TableCell align="center">{localeCommon['impersonate']}</TableCell>}
                {/* <TableCell align="center">{localeUsers['seatType']}</TableCell> */}
                <TableCell align="center">{localeCommon['actions']}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((row, idx) => (
                <TableRow key={`${idx}-${row.firstName}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" title={row.firstName}>
                    {row.firstName &&
                      row.lastName &&
                      (row.firstName + ' ' + row.lastName).slice(0, 35) +
                        ((row.firstName + ' ' + row.lastName).length > 35 ? '...' : '')}
                  </TableCell>
                  <TableCell component="th" scope="row" title={row.email}>
                    {row.email && row.email.slice(0, 45) + (row.email.length > 45 ? '...' : '')}
                  </TableCell>
                  {role !== UserRoles.SUPER_ADMIN && (
                    <TableCell component="th" scope="row">
                      {row.role}
                    </TableCell>
                  )}
                  {role === UserRoles.SUPER_ADMIN && <TableCell component="th" scope="row">
                    {!row?.failedLoginAttempts ? '-' : row?.failedLoginAttempts}
                    {Number(row?.failedLoginAttempts) > 0 && (
                      <WolfTooltip title={localeUsers['resetFailedLoginAttempts']}>
                        <IconButton onClick={() => resetFailedLoginCount(row?.userId || '')}>
                          <RestartAltOutlined color="error" sx={{ margin: 1 }} />
                        </IconButton>
                      </WolfTooltip>
                    )}
                  </TableCell>}
                  {role === UserRoles.SUPER_ADMIN && (
                    <TableCell component="th" scope="row">
                      <Box
                        className="flex justify-center items-center"
                        onClick={() => handleImpersonate(row?.userId, row?.firstName + ' ' + row?.lastName)}>
                        <WolfTooltip title={localeCommon['impersonate']}>
                          <IconButton>
                            <Person2Outlined />
                          </IconButton>
                        </WolfTooltip>
                      </Box>
                    </TableCell>
                  )}
                  {/* <TableCell align="center">
                    <Box className="flex justify-center items-center gap-2">
                      {row.hasSeat ? (
                        <>
                          <Chip
                            label={row.seatType === 'ADMIN' ? localeUsers['adminSeat'] : localeUsers['userSeat']}
                            color="primary"
                            size="small"
                          />
                          {row.status === UserStatus.ACTIVE && role !== UserRoles.USER && (
                            <WolfTooltip title={localeUsers['revokeSeat']}>
                              <IconButton onClick={() => handleRevokeSeat(row.seatAssignmentId)} size="small">
                                <EventSeat fontSize="small" />
                              </IconButton>
                            </WolfTooltip>
                          )}
                        </>
                      ) : (
                        <>
                          <Chip label={localeUsers['noSeat']} size="small" />
                          {row.status === UserStatus.ACTIVE && [UserRoles.SUPER_ADMIN, UserRoles.OWNER, UserRoles.ADMIN].includes(role as UserRoles) && (
                            <WolfTooltip title={localeUsers['assignSeat']}>
                              <IconButton onClick={() => handleAssignSeat(row.userId || '', row.role)} size="small">
                                <EventSeat fontSize="small" color="primary" />
                              </IconButton>
                            </WolfTooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell> */}
                  <TableCell align="right" sx={{ textAlign: 'center' }} width={140}>
                    <Box className="flex justify-center items-center">
                      {role !== UserRoles.USER && (
                        <WolfTooltip title={localeCommon['edit']}>
                          <IconButton onClick={() => toggleEditModal(row)}>
                            <EditIcon24 color="error" sx={{ margin: 1 }} />
                          </IconButton>
                        </WolfTooltip>
                      )}
                      {/* Use organizationStatus for organization-level enable/disable (non-SUPER_ADMIN) */}
                      {row.organizationStatus === UserStatus.ACTIVE && role !== UserRoles.USER && role !== UserRoles.SUPER_ADMIN && (
                        <WolfTooltip title={localeCommon['disable']}>
                          <IconButton onClick={() => toggleConfirmDeleteModal(row)}>
                            <NoAccounts color="error" sx={{ margin: 1 }} />
                          </IconButton>
                        </WolfTooltip>
                      )}
                      {row.organizationStatus === UserStatus.DISABLED && role !== UserRoles.SUPER_ADMIN && (
                        <WolfTooltip title={localeCommon['enable']}>
                          <IconButton onClick={() => toggleEnableConfirmModal(row)}>
                            <RestoreFromTrash color="error" sx={{ margin: 1 }} />
                          </IconButton>
                        </WolfTooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      <CustomModal
        isOpen={showConfirmDisableModal}
        onClose={modalClose}
        title={localeUsers['disableUser']}
        showButtons={true}
        onConfirm={disableUser}
        customConfirmColor="error"
        maxWidth="600px"
        customConfirmText={localeCommon['disable']}>
        <DisableUserModal user={userToDisable} />
      </CustomModal>

      <CustomModal
        isOpen={showConfirmEnableModal}
        onClose={modalClose}
        showButtons={true}
        onConfirm={enableUser}
        maxWidth="600px"
        title={localeUsers['enableUser']}>
        <EnableUserModal user={userToEnable} />
      </CustomModal>

      <CustomModal isOpen={!!showEditModal} maxWidth="600px" onClose={modalClose} title={localeUsers['editUser']}>
        {/* //TODO adjust saving */}
        <CreateEditUserModal
          editing={true}
          modalClose={modalClose}
          editUser={handleEditUser}
          organizations={organizations}
          saving={false}
          initialData={{
            userId: userToEdit?.userId || '',
            email: userToEdit?.email || '',
            firstName: userToEdit?.firstName || '',
            lastName: userToEdit?.lastName || '',
            role: userToEdit?.role || UserRoles.USER,
            locale: userToEdit?.locale || UserLocale.EN,
            seatAssignmentId: userToEdit?.seatAssignmentId,
          }}
        />
      </CustomModal>
    </>
  );
};

export default UserList;
